import { useCallback, useEffect, useMemo } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { EditorConfig } from '@editor/interfaces/editor'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor, setWorkingElementId } from '@editor/store/tool'
import { useSelector } from 'react-redux'

import { reset } from '../store'

export default function useEditor(): EditorConfig {
  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const prevSelectedTool = useSelector((state: RootState) => state.blueprint.editor.prevSelectedTool)

  // Flags
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.Pen, [selectedTool])
  const isPreviousTool = useCallback((tool: BlueprintEditorTools) => prevSelectedTool === tool, [prevSelectedTool])

  /**
   * When user changes tools (in or out of Polyline tool), update cursor.
   */
  useEffect(() => {
    if (isToolSelected) {
      dispatch(setCursor(CursorState.CROSSHAIR))
    } else if (isPreviousTool(BlueprintEditorTools.Pen) && selectedTool !== BlueprintEditorTools.Focus) {
      dispatch(setCursor(CursorState.DEFAULT))
      dispatch(reset())
      dispatch(setWorkingElementId(undefined))
    }
  }, [isToolSelected, selectedTool, isPreviousTool, dispatch])

  /**
   * Reset on unmount
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  return {
    buttons: {
      reset: {
        onClick: useCallback(() => dispatch(reset()), [dispatch]),
        isShown: useCallback(() => isToolSelected, [isToolSelected]),
        isDisabled: useCallback(() => false, []),
      },
      undo: {
        isShown: useCallback(() => isToolSelected, [isToolSelected]),
        isDisabled: useCallback(() => false, []),
      },
    },
  }
}
